import { useEffect } from 'react';
import { IS_DEV } from '../../config/env';

/**
 * Keeps `document.body` / `document.documentElement` pinned at scroll offset 0.
 *
 * The app shell is a 100vh box and every scrollable region inside it owns its
 * own `overflow: auto` container, so `html, body` are `overflow: hidden`
 * (`theme/styles.ts`). That hides the scrollbar — it does NOT make the document
 * unscrollable. `element.focus()` without `preventScroll`, `scrollIntoView()`,
 * and anchor navigation all still scroll it programmatically, and once they do
 * there is no scrollbar and no wheel gesture that can bring it back.
 *
 * The visible symptom is severe and looks nothing like a scroll bug: the app
 * shell slides up under the viewport edge, taking the sticky `DetailTopBar`
 * with it, while the `position: fixed` nav rail stays exactly where it was. It
 * reads as "the header vanished", and it persists until a reload.
 *
 * The instance we hit (2026-08-23) was a portalled Chakra `MenuList` taller
 * than the viewport. The `maxH` cap on `Menu.list` (`theme/components.ts`)
 * removes that precondition only for surfaces anchored within 120px of the
 * viewport top — the reported kebab, at ~56, is one. A menu anchored further
 * down still overflows and still scrolls the document (measured: anchor 232 in
 * a 620px viewport scrolls `body` by 113px even with the cap applied), so this
 * hook is what covers real menus in the sidebar tree and in list rows today.
 * It is load-bearing, not a spare.
 *
 * Scroll events do not bubble, so listening on these targets catches only
 * their own scrolling — the app's real scroll containers are untouched and pay
 * nothing. `document` is in the list because that is where a scroll of the
 * VIEWPORT is dispatched (not at `documentElement`), which is the shape this
 * would take if `html` ever stopped being `overflow: hidden`. Today only
 * `body` ever fires; the other two cost nothing and stop the hook silently
 * doing nothing in the one scenario it exists to survive.
 *
 * The sibling surface is Chakra's `Popover`, which also portals to <body> and
 * also focuses without `preventScroll` (`use-popover.mjs` passes an options
 * object with the field absent, so the `defaultOptions` `true` never applies).
 * It is materially less exposed — it focuses the FIRST FOCUSABLE CHILD, which
 * sits at the top of the content and is normally already on screen, where a
 * menu focuses the whole list box and a list taller than the viewport gets
 * start-aligned. That is why `Popover.content` is deliberately NOT capped here:
 * the cap would clip any non-portalled popper nested inside it, for a much
 * smaller risk. This hook is what covers it instead.
 *
 * This CORRECTS, it does not prevent: `scroll` is dispatched asynchronously, so
 * a measured repro shows the offset applied and then snapped back on the next
 * frame. That is a flicker at worst, versus chrome that stays off-screen until
 * a reload. Prevention is the `maxH` cap's job.
 */
export function useDocumentScrollLock(): void {
  useEffect(() => {
    // Reset both elements; listen on both plus `document` (see header).
    const targets = [document.body, document.documentElement];
    const listenOn: EventTarget[] = [...targets, document];

    const reset = () => {
      for (const el of targets) {
        if (el.scrollTop === 0 && el.scrollLeft === 0) continue;
        // Loud in dev, because silently correcting is how the next instance of
        // this class stays invisible (Bower rule 12). Reaching here at all
        // means something extended the document past the viewport and then
        // scrolled it — worth finding, not just undoing.
        if (IS_DEV) {
          console.warn(
            `[useDocumentScrollLock] ${el.tagName.toLowerCase()} was scrolled to ` +
              `(${el.scrollLeft}, ${el.scrollTop}) and has been reset. Something portalled ` +
              `is taller than the viewport — cap its height instead of relying on this.`,
          );
        }
        el.scrollTop = 0;
        el.scrollLeft = 0;
      }
    };

    // A scroll may already have happened before this mounted (e.g. a deep link
    // with a fragment), so correct the current position too, not just future
    // events.
    reset();

    for (const target of listenOn) target.addEventListener('scroll', reset);
    return () => {
      for (const target of listenOn) target.removeEventListener('scroll', reset);
    };
  }, []);
}
